import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getProductReviews } from "@/lib/api";

export const StarRating = ({ value = 0, size = 14, color = "#E8B547" }) => {
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  return (
    <div className="inline-flex items-center gap-0.5" aria-label={`${value} of 5 stars`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const isFilled = i < full || (i === full && half);
        return (
          <Star
            key={i}
            style={{ width: size, height: size }}
            fill={isFilled ? color : "transparent"}
            stroke={color}
            strokeWidth={1.6}
          />
        );
      })}
    </div>
  );
};

const formatDateNo = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("nb-NO", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

export const ProductReviews = ({ slug }) => {
  const { lang } = useLanguage();
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setLoading(true);
    getProductReviews(slug)
      .then(setData)
      .catch(() => setData({ reviews: [], count: 0, average: 0 }))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return null;
  if (data.count === 0) return null;

  const visible = showAll ? data.reviews : data.reviews.slice(0, 3);

  return (
    <section className="mt-16 pt-12 border-t border-[#E5E2DC]" data-testid="product-reviews">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#D97B66] font-medium mb-3">
            {lang === "en" ? "Reviews" : "Anmeldelser"}
          </p>
          <h3 className="font-heading text-2xl md:text-3xl text-[#2C302E] font-light">
            {lang === "en"
              ? "What other parents say"
              : "Hva andre foreldre sier"}
          </h3>
        </div>

        <div className="bg-white border border-[#E5E2DC] rounded-2xl px-5 py-3 flex items-center gap-4">
          <div>
            <p className="font-heading text-3xl text-[#2C302E] font-medium leading-none">
              {data.average.toFixed(1)}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-[#6B726C] mt-1">
              {lang === "en" ? "of 5" : "av 5"}
            </p>
          </div>
          <div className="border-l border-[#E5E2DC] pl-4">
            <StarRating value={data.average} size={16} />
            <p className="text-xs text-[#6B726C] mt-1">
              {data.count}{" "}
              {lang === "en"
                ? data.count === 1
                  ? "review"
                  : "reviews"
                : data.count === 1
                ? "anmeldelse"
                : "anmeldelser"}
            </p>
          </div>
        </div>
      </div>

      <ul className="space-y-4">
        {visible.map((r) => (
          <li
            key={r.id}
            data-testid={`review-${r.id}`}
            className="bg-white border border-[#E5E2DC] rounded-2xl p-5 md:p-6"
          >
            <div className="flex items-center gap-3 flex-wrap mb-3">
              <StarRating value={r.stars} size={14} />
              <span className="text-[10px] uppercase tracking-widest text-[#8BA896] font-semibold bg-[#EFF5EE] px-2 py-0.5 rounded-full">
                {lang === "en" ? "Verified purchase" : "Verifisert kjøp"}
              </span>
            </div>
            <p className="font-heading text-base text-[#2C302E] font-medium">
              {r.customer_name}
              <span className="ml-2 text-xs text-[#6B726C] font-normal">
                · {formatDateNo(r.approved_at || r.created_at)}
              </span>
            </p>
            {r.comment && (
              <p className="mt-2 text-sm text-[#4A4F4A] leading-relaxed whitespace-pre-line">
                {r.comment}
              </p>
            )}
          </li>
        ))}
      </ul>

      {data.reviews.length > 3 && (
        <button
          data-testid="reviews-show-more"
          onClick={() => setShowAll((v) => !v)}
          className="mt-5 text-sm text-[#D97B66] hover:text-[#C66A55] font-medium"
        >
          {showAll
            ? lang === "en"
              ? "Show fewer"
              : "Vis færre"
            : lang === "en"
            ? `Show all ${data.reviews.length} reviews`
            : `Vis alle ${data.reviews.length} anmeldelser`}
        </button>
      )}
    </section>
  );
};
